/**
 * Voice Button Component
 *
 * Microphone button for voice input with recording and transcription.
 * Shows visible error feedback when transcription fails.
 * Mic is always visible — local Whisper model loads lazily in background.
 */

import { Mic, Square, Loader2, AlertCircle } from 'lucide-react';
import clsx from 'clsx';
import { useVoiceRecording } from '../hooks/useVoiceRecording';
import { useTranscription } from '../hooks/useTranscription';
import { useSettings } from '../hooks/useSettings';
import { useState, useEffect, useRef } from 'react';
import { loadWhisperModel, isWhisperSupported } from '../lib/whisper-local';
import * as Sentry from '@sentry/react';

interface VoiceButtonProps {
  onTranscription: (text: string) => void;
  disabled?: boolean;
}

export function VoiceButton({ onTranscription, disabled = false }: VoiceButtonProps) {
  const { settings } = useSettings();
  const recording = useVoiceRecording({ maxDuration: 120 });

  const transcriptionMethod = settings.transcriptionMethod || 'local-tiny';
  const forceApi = transcriptionMethod === 'api';
  const modelSize = transcriptionMethod.startsWith('local-')
    ? (transcriptionMethod.replace('local-', '') as 'tiny' | 'base' | 'small')
    : 'tiny';

  const transcription = useTranscription({
    forceApi,
    modelSize,
    prompt: settings.transcriptionVocabulary || undefined,
  });
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Clear error after 5 seconds
  useEffect(() => {
    if (!error) return;
    const timer = setTimeout(() => setError(null), 5000);
    return () => clearTimeout(timer);
  }, [error]);

  // Lazy model loading — non-blocking
  const loadAttempted = useRef(false);
  useEffect(() => {
    if (forceApi || loadAttempted.current) return;
    if (!isWhisperSupported()) return;
    loadAttempted.current = true;
    loadWhisperModel(modelSize).catch(() => {});
  }, [forceApi, modelSize]);

  const handleClick = async () => {
    if (!recording.isSupported) return;
    setError(null);

    if (recording.isRecording) {
      try {
        setIsTranscribing(true);
        const audioBlob = await recording.stopRecording();
        const text = await transcription.transcribe(audioBlob);
        if (text?.trim()) {
          onTranscription(text.trim());
        } else {
          setError('No speech detected');
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Transcription failed';
        setError(message);
        Sentry.captureException(err, { tags: { component: 'VoiceButton', action: 'transcribe' } });
      } finally {
        setIsTranscribing(false);
      }
    } else {
      try {
        await recording.startRecording();
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Could not access microphone';
        setError(message);
        Sentry.captureException(err, { tags: { component: 'VoiceButton', action: 'startRecording' } });
      }
    }
  };

  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (!recording.isSupported) return null;

  return (
    <div className="relative flex-shrink-0">
      <button
        type="button"
        onClick={handleClick}
        disabled={disabled || isTranscribing}
        className={clsx(
          'p-2.5 rounded-lg transition-all relative',
          recording.isRecording &&
            'bg-red-50 dark:bg-red-900/20 text-red-500 dark:text-red-400 animate-pulse',
          !recording.isRecording &&
            !isTranscribing &&
            !error &&
            'text-text-tertiary-light dark:text-text-tertiary-dark hover:bg-surface-hover-light dark:hover:bg-surface-hover-dark',
          isTranscribing && 'text-primary-500 dark:text-primary-400',
          error && 'text-red-500 dark:text-red-400',
          disabled && 'opacity-50 cursor-not-allowed',
        )}
        aria-label={
          error
            ? error
            : recording.isRecording
              ? 'Stop recording'
              : isTranscribing
                ? 'Transcribing...'
                : 'Start voice recording'
        }
        title={
          error
            ? error
            : recording.isRecording
              ? `Recording... ${formatDuration(recording.duration)}`
              : 'Record voice message'
        }
      >
        {isTranscribing ? (
          <Loader2 className="w-5 h-5 animate-spin" />
        ) : error ? (
          <AlertCircle className="w-5 h-5" />
        ) : recording.isRecording ? (
          <div className="relative">
            <Square className="w-5 h-5 fill-current" />
            {recording.duration > 0 && (
              <span className="absolute -top-6 left-1/2 -translate-x-1/2 px-1.5 py-0.5 bg-red-500 text-white text-xs md:text-[10px] font-mono rounded whitespace-nowrap">
                {formatDuration(recording.duration)}
              </span>
            )}
          </div>
        ) : (
          <Mic className="w-5 h-5" />
        )}
      </button>

      {/* Error tooltip — visible above the button */}
      {error && (
        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1.5 bg-red-500 text-white text-xs rounded-lg whitespace-nowrap shadow-lg z-50">
          {error}
          <div className="absolute top-full left-1/2 -translate-x-1/2 w-2 h-2 bg-red-500 rotate-45 -mt-1" />
        </div>
      )}
    </div>
  );
}
